import type { ReactNode } from 'react';
import {
  isAllowedHref,
  type RichTextDocument,
  type RichTextInline,
  type RichTextListItem,
  type RichTextMark,
  type RichTextNode,
} from '@oscom/shared';

/**
 * Stored rich text -> React elements. Never HTML: every text goes through
 * React as a text node, unknown nodes render nothing, and a link is a link
 * only with an allowed scheme (design 2026-10-31). Stored data is not trusted
 * to match its type: malformed nodes render nothing instead of throwing.
 */
export function RichTextView({ doc }: { doc: RichTextDocument }): ReactNode {
  return (
    <div className="rich-text">
      {list<RichTextNode>(doc?.content).map((node, index) => (
        <BlockNode key={index} node={node} />
      ))}
    </div>
  );
}

/** The array itself, or an empty one for anything that is not an array. */
function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function isNode(value: unknown): value is { type: unknown } {
  return typeof value === 'object' && value !== null;
}

function BlockNode({ node }: { node: RichTextNode }): ReactNode {
  if (!isNode(node)) return null;
  switch (node.type) {
    case 'paragraph':
      return (
        <p>
          {list<RichTextInline>(node.content).map((inline, index) => (
            <InlineNode key={index} node={inline} />
          ))}
        </p>
      );
    case 'bulletList':
      return (
        <ul>
          {list<RichTextListItem>(node.content).map((item, index) => (
            <ListItem key={index} item={item} />
          ))}
        </ul>
      );
    case 'orderedList': {
      const start: unknown = node.attrs?.start;
      return (
        <ol start={typeof start === 'number' && Number.isInteger(start) ? start : undefined}>
          {list<RichTextListItem>(node.content).map((item, index) => (
            <ListItem key={index} item={item} />
          ))}
        </ol>
      );
    }
    default:
      return null;
  }
}

function ListItem({ item }: { item: RichTextListItem }): ReactNode {
  if (!isNode(item)) return null;
  return (
    <li>
      {list<RichTextNode>(item.content).map((child, index) => (
        <BlockNode key={index} node={child} />
      ))}
    </li>
  );
}

function hasMark(marks: readonly RichTextMark[], type: RichTextMark['type']): boolean {
  return marks.some((mark) => isNode(mark) && mark.type === type);
}

/** The href of the first link mark when it is allowed, otherwise undefined. */
function allowedLink(marks: readonly RichTextMark[]): string | undefined {
  const link = marks.find(
    (mark): mark is Extract<RichTextMark, { type: 'link' }> => isNode(mark) && mark.type === 'link',
  );
  const href: unknown = link?.attrs?.href;
  return isAllowedHref(href) ? href : undefined;
}

function InlineNode({ node }: { node: RichTextInline }): ReactNode {
  if (!isNode(node)) return null;
  if (node.type === 'hardBreak') return <br />;
  if (node.type !== 'text' || typeof node.text !== 'string') return null;
  const marks = list<RichTextMark>(node.marks);
  let element: ReactNode = node.text;
  if (hasMark(marks, 'italic')) element = <em>{element}</em>;
  if (hasMark(marks, 'bold')) element = <strong>{element}</strong>;
  if (hasMark(marks, 'placeholder')) {
    element = <span className="rt-placeholder">{element}</span>;
  }
  const href = allowedLink(marks);
  if (href) {
    element = (
      <a href={href} rel="noopener noreferrer">
        {element}
      </a>
    );
  }
  return element;
}
